import classNames from 'classnames';
import { FC, useMemo } from 'react';
import type { RegulationData, TileData } from 'state/quotes/dashboard/model';
import type { PinnedGroup } from 'state/quotes/dashboard/selectors';
import type { TileMetadata } from 'state/quotes/dashboard/tiles/tiles.model';

import { AdditionalQuoteInfo } from './components/AdditionalQuoteInfo';
import { Footer } from './components/Footer';
import { TileHeader } from './components/headers';
import { Product } from './components/Product';
import { Underlyings } from './components/underlyings';
import { FrozenTile } from './Tile.frozen';
import { QuotedTile } from './Tile.quoted';
import { QuotingTile } from './Tile.quoting';
import { RejectedWithoutPriceTile } from './Tile.rejected.withoutPrice';
import { RejectedWithPriceTile } from './Tile.rejected.withPrice';
import { TechnicalErrorWithoutPriceTile } from './Tile.technicalError.withoutPrice';
import { TechnicalErrorWithPriceTile } from './Tile.technicalError.withPrice';
import { TradeableTile } from './Tile.tradable';
import { TradedTile } from './Tile.traded';
import { TradingTile } from './Tile.trading';

export type TileProps<T> = {
  quoteId: string;
  hasAssociatedTab: boolean;
  className?: string;
  data: T;
  metadata: TileMetadata;
  regulationData: RegulationData;
  customGroups: PinnedGroup[];

  onWorkspaceTabOpened: () => void;
  onClosed: () => void;
  onReprice?: () => void;
  onPinToggled: () => void;
  onGroupChanged(group: string): void;
  onUnGrouped(): void;
};

export const RawTile: FC<TileProps<TileData>> = ({
  data,
  quoteId,
  hasAssociatedTab,
  metadata: { isPinned, groupName, groupColor },
  regulationData,
  customGroups,
  onWorkspaceTabOpened,
  onClosed,
  onReprice,
  onPinToggled,
  onGroupChanged,
  onUnGrouped,
}) => {
  const { clientId, clientName, externalQuoteId, subProduct, wrapper, underlyings, maturityDate, tenor, currency } =
    data;

  const header = useMemo(
    () => (
      <>
        <TileHeader
          clientId={clientId}
          clientName={clientName}
          quoteId={externalQuoteId ?? quoteId}
          onClosed={onClosed}
        />
      </>
    ),

    [clientId, clientName, externalQuoteId, onClosed, quoteId],
  );

  const subHeader = useMemo(
    () => (
      <>
        <Product
          subProduct={subProduct}
          wrapper={wrapper}
          className={classNames({ 'pb-3 mb-1': maturityDate === undefined && tenor === undefined })}
        />
        <AdditionalQuoteInfo currency={currency} maturityDate={maturityDate} tenor={tenor} />
        {underlyings !== undefined && <Underlyings data={underlyings} id={quoteId} />}
      </>
    ),
    [currency, maturityDate, quoteId, subProduct, tenor, underlyings, wrapper],
  );

  const footer = useMemo(
    () => (
      <Footer
        hasAssociatedTab={hasAssociatedTab}
        isPinned={isPinned}
        availableGroups={customGroups}
        selectedGroup={groupName}
        selectedGroupColor={groupColor}
        onWorkspaceTabOpened={onWorkspaceTabOpened}
        regulationData={regulationData}
        onPinToggled={onPinToggled}
        onReprice={onReprice}
        onGroupChanged={onGroupChanged}
        onUnGrouped={onUnGrouped}
      />
    ),
    [
      groupColor,
      customGroups,
      groupName,
      hasAssociatedTab,
      isPinned,
      onGroupChanged,
      onPinToggled,
      onReprice,
      onUnGrouped,
      onWorkspaceTabOpened,
      regulationData,
    ],
  );

  switch (data.status) {
    case 'Quoting':
      if (hasAssociatedTab) {
        return <QuotingTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />;
      }
      break;
    case 'Quoted':
      return <QuotedTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />;
    case 'RejectedWithoutPrice':
      return (
        <RejectedWithoutPriceTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />
      );
    case 'RejectedWithPrice':
      return (
        <RejectedWithPriceTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />
      );
    case 'TechnicalErrorWithoutPrice':
      return (
        <TechnicalErrorWithoutPriceTile
          header={header}
          subHeader={subHeader}
          footer={footer}
          data={data}
          quoteId={quoteId}
        />
      );
    case 'Tradeable':
      return <TradeableTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />;
    case 'TechnicalErrorWithPrice':
      return (
        <TechnicalErrorWithPriceTile
          header={header}
          subHeader={subHeader}
          footer={footer}
          data={data}
          quoteId={quoteId}
        />
      );
    case 'Trading':
      if (hasAssociatedTab) {
        return <TradingTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />;
      }
      break;
    case 'Traded':
      return <TradedTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />;
    default:
      break;
  }

  return <FrozenTile header={header} subHeader={subHeader} footer={footer} data={data} quoteId={quoteId} />;
};

RawTile.displayName = 'RawTile';
